import { useCallback } from "react";
import { toast } from "react-toastify";

export const useQuoteItemTableUpdater = (quoteItems, updateQuoteItem, updateQuoteItems) => {

    const isBasketProcessingItem = useCallback((item) => {
        return item?.source?.toLocaleLowerCase() === "basket processing";
    }, []);

    /**
     * Spreads a total price or cost value entered on a group row across all child items,
     * calculating a new per-unit value for each item proportionally to its original values.
     *
     * This solves the "penny allocation" problem — when a total is split across items with
     * different quantities and recurring payment schedules, floating point rounding can cause
     * the sum of individual item totals to drift from the target. This function guarantees
     * that the sum of all allocated unit values × quantities matches the target total exactly.
     *
     * Algorithm overview:
     *   1. Each item's share of the total is calculated proportionally to its original unit value × quantity.
     *   2. Shares are floored to whole pennies, and leftover pennies are distributed to items with the
     *      largest fractional remainders (Largest Remainder Method).
     *   3. After converting back to unit prices, any rounding drift is corrected by adding/subtracting
     *      pennies from items starting with the highest-impact (largest quantity × payments) items.
     *   4. A final swap pass resolves any remaining ±1 penny difference that the iterative pass couldn't fix.
     *
     * Supported field types:
     *   - totalUpfront* (e.g. "totalUpfrontPrice", "totalUpfrontCost"):
     *       Spreads the value across items using quantity only.
     *   - totalRecurring* / totalContract* / totalAnnual* (e.g. "totalRecurringPrice"):
     *       Spreads the value across items using quantity × recurringNumPayments.
     *       Items with zero quantity, zero recurringIntervalMonths, or zero recurringNumPayments are excluded.
     *
     * Only "total*" prefixed fields are currently handled. Other field prefixes are silently ignored.
     *
     * @param {string}   field  - The total field key being edited (e.g. "totalUpfrontPrice", "totalRecurringCost").
     * @param {number}   value  - The new total value entered by the user.
     * @param {object[]} items  - The child item rows belonging to the group. Each must have `guid`, `quantity`,
     *                           `recurringIntervalMonths`, `recurringNumPayments`, and the original unit field
     *                           (e.g. `originalUpfrontPrice`).
     */
    const spreadValueToItems = useCallback((field, value, items) => {
        let unitField = "";       // The unit-level field name derived from the total field (e.g., 'unitPrice' from 'totalPrice')
        let itemUpdates = [];     // Array to store updated item unit values after allocation

        if (field.startsWith("total")) {
            // Derive the unit field by removing "total" prefix and converting first letter to lowercase
            // e.g. totalUpfrontCost -> upfrontCost
            unitField = field.replace("total", "").charAt(0).toLowerCase() + field.replace("total", "").slice(1);
            // Derive the original unit field name for referencing original per-unit values (e.g., originalUpfrontCost)
            const originalUnitField = field.replace("total", "original");

            // Main function to allocate the totalValue to individual items while respecting pennies and quantities
            const allocatePennies = (totalValue, items, originalUnitField, quantityField = "quantity", recurringMonthsField = null, recurringPaymentsField = null) => {
                // Convert total value to pennies to avoid floating point rounding issues
                const totalValuePennies = Math.round(totalValue * 100);

                // Calculate total of original values (in pennies) across all items,
                // factoring in quantity and recurring payments if applicable
                const totalOriginalValuePennies = items.reduce((sum, item) => {
                    // Base value is unit value multiplied by quantity
                    const base = item[originalUnitField] * item[quantityField];
                    // Recurring multiplier, if fields are specified; else default 1
                    const recurringMultiplier = recurringMonthsField && recurringPaymentsField
                        ? item[recurringPaymentsField]
                        : 1;
                    // Add rounded value in pennies to running total
                    return sum + Math.round(base * recurringMultiplier * 100);
                }, 0);

                // Guard: if all original values are zero, fall back to an equal-weight split by
                // quantity × recurringMultiplier so that any non-zero total is still allocated
                // sensibly rather than producing Infinity/NaN from the division below.
                if (totalOriginalValuePennies <= 0) {
                    // Compute each item's weight (quantity × recurringMultiplier)
                    const weights = items.map(item => {
                        const recurringMultiplier = recurringMonthsField && recurringPaymentsField
                            ? item[recurringPaymentsField]
                            : 1;
                        return item[quantityField] * recurringMultiplier;
                    });
                    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
                    if (totalWeight <= 0) {
                        // Nothing to distribute into (e.g. all quantities are 0) — return each item's existing unit value unmodified
                        return items.map(item => ({ guid: item.guid, [unitField]: item[unitField] }));
                    }

                    // Allocate totalValuePennies across items proportional to weight using the
                    // largest-remainder method, so the sum of all allocations equals the target exactly.
                    const exactShares = weights.map(w => (w / totalWeight) * totalValuePennies);
                    const flooredShares = exactShares.map(s => Math.floor(s));
                    const remainders = exactShares.map((s, i) => s - flooredShares[i]);

                    const allocatedPennies = flooredShares.reduce((sum, s) => sum + s, 0);
                    const remainingPennies = totalValuePennies - allocatedPennies;

                    // Distribute leftover pennies to items with the largest fractional remainders
                    const sortedIndices = items.map((_, i) => i).sort((a, b) => remainders[b] - remainders[a]);
                    for (let i = 0; i < remainingPennies; i++) {
                        flooredShares[sortedIndices[i % sortedIndices.length]] += 1;
                    }

                    // Convert each item's allocated total pennies back to a unit price
                    return items.map((item, i) => ({
                        guid: item.guid,
                        [unitField]: Number((flooredShares[i] / weights[i] / 100).toFixed(2))
                    }));
                }

                // Calculate initial allocations for each item:
                // For each item, calculate the exact share of the totalValue in pennies
                // based on proportion of its original value to the total original value
                let allocations = items.map(item => {
                    const base = item[originalUnitField] * item[quantityField];
                    const recurringMultiplier = recurringMonthsField && recurringPaymentsField
                        ? item[recurringPaymentsField]
                        : 1;
                    // Exact share before rounding, in pennies (can be fractional)
                    const exactSharePennies = (base * recurringMultiplier * 100 * totalValuePennies) / totalOriginalValuePennies;
                    // Floor the share to pennies (round down)
                    const flooredShare = Math.floor(exactSharePennies);
                    // Fractional remainder after flooring
                    const remainder = exactSharePennies - flooredShare;
                    return {
                        ...item,
                        exactSharePennies,
                        flooredShare,
                        remainder,
                        quantity: item[quantityField],
                        recurringMultiplier
                    };
                });

                // Sum all the floored shares in pennies to find how many pennies have been allocated so far
                let allocatedPennies = allocations.reduce((sum, item) => sum + item.flooredShare, 0);
                // Calculate leftover pennies that still need to be allocated
                let remainingPennies = totalValuePennies - allocatedPennies;

                // Sort items descending by remainder so we distribute leftover pennies starting from highest remainder
                allocations.sort((a, b) => b.remainder - a.remainder);
                // Distribute leftover pennies one by one to items with highest remainder in round-robin manner
                for (let i = 0; i < remainingPennies; i++) {
                    allocations[i % allocations.length].flooredShare += 1;
                }

                // Convert flooredShare pennies back into rounded unit values per item
                let roundedAllocations = allocations.map(item => {
                    // Calculate raw unit value (pennies / quantity / recurringMultiplier / 100 to get dollars)
                    const rawUnitValue = item.flooredShare / item.quantity / item.recurringMultiplier / 100;
                    // Round unit value to 2 decimal places
                    const roundedUnit = Number(rawUnitValue.toFixed(2));
                    return {
                        guid: item.guid,             // Keep track of item by unique ID
                        [unitField]: roundedUnit,    // Set unit field with the rounded unit value
                        quantity: item.quantity,
                        recurringMultiplier: item.recurringMultiplier,
                        originalFlooredShare: item.flooredShare  // Keep original floored pennies for reference
                    };
                });

                // Calculate total sum of all rounded unit values * quantities * recurring multipliers
                let totalAfterRounding = roundedAllocations.reduce((sum, item) => {
                    return sum + item[unitField] * item.quantity * item.recurringMultiplier;
                }, 0);

                // Calculate the difference between total after rounding and desired total value (rounded to 2 decimals)
                let difference = Number((totalAfterRounding - totalValue).toFixed(2));
                const penny = 0.01;  // Constant representing one penny

                // Helper function to check if we can adjust a unit price by delta pennies:
                // - If delta < 0 (reduce), only allowed if current unit price >= 1 penny (avoid negative prices)
                // - If delta > 0 (increase), always allowed
                const canAdjust = (item, delta) => {
                    if (delta < 0) return item[unitField] >= penny;
                    return true;
                };

                // Helper function to apply delta pennies adjustment to unit price, keeping 2 decimal precision
                const applyAdjustment = (item, delta) => {
                    item[unitField] = Number((item[unitField] + delta).toFixed(2));
                };

                // If the difference between allocated total and desired total is significant (>= half a penny),
                // attempt to fix it by adjusting individual item unit prices
                if (Math.abs(difference) >= 0.005) {
                    if (difference > 0) {
                        // Overshoot: total is too high, so try subtracting pennies from biggest impact items first
                        // "Impact" = quantity * recurringMultiplier (how much reducing 1 penny in unit price affects total)
                        roundedAllocations.sort((a, b) => (b.quantity * b.recurringMultiplier) - (a.quantity * a.recurringMultiplier));

                        // Iterate over items and subtract pennies as long as difference remains
                        for (let i = 0; i < roundedAllocations.length && difference >= 0.005; i++) {
                            const item = roundedAllocations[i];
                            const impact = item.quantity * item.recurringMultiplier;
                            // While we still have enough difference to subtract from this item and can adjust unit price
                            while (difference >= impact * penny && canAdjust(item, -penny)) {
                                applyAdjustment(item, -penny);
                                difference -= impact * penny;
                                difference = Number(difference.toFixed(2)); // Re-round difference to avoid floating issues
                            }
                        }
                    } else {
                        // Undershoot: total is too low, try adding pennies to smallest impact items first
                        roundedAllocations.sort((a, b) => (a.quantity * a.recurringMultiplier) - (b.quantity * b.recurringMultiplier));

                        difference = Math.abs(difference);
                        // Iterate over items and add pennies as long as difference remains
                        for (let i = 0; i < roundedAllocations.length && difference >= 0.005; i++) {
                            const item = roundedAllocations[i];
                            const impact = item.quantity * item.recurringMultiplier;
                            while (difference >= impact * penny) {
                                applyAdjustment(item, penny);
                                difference -= impact * penny;
                                difference = Number(difference.toFixed(2));
                            }
                        }
                    }
                }

                // Final attempt to fix any remaining tiny difference by swapping pennies between items:
                // Subtract 1 penny from a big impact item and add 1 penny to a small impact item if this fixes the difference exactly
                if (difference >= 0.005) {
                    // Filter items with at least 1 penny unit price and sort descending by impact for subtraction candidates
                    const bigImpactItems = roundedAllocations.filter(item => item[unitField] >= penny)
                        .sort((a, b) => (b.quantity * b.recurringMultiplier) - (a.quantity * a.recurringMultiplier));
                    // Sort ascending by impact for addition candidates
                    const smallImpactItems = [...roundedAllocations].sort((a, b) => (a.quantity * a.recurringMultiplier) - (b.quantity * b.recurringMultiplier));

                    // Try all combinations of big impact and small impact items for a swap
                    for (const bigItem of bigImpactItems) {
                        for (const smallItem of smallImpactItems) {
                            if (bigItem.guid === smallItem.guid) continue; // Skip if same item
                            const bigImpact = bigItem.quantity * bigItem.recurringMultiplier;
                            const smallImpact = smallItem.quantity * smallItem.recurringMultiplier;
                            const impactDiff = bigImpact - smallImpact;
                            // If the difference equals the impact difference times a penny and bigItem can lose a penny, do the swap
                            if (impactDiff * penny === difference && bigItem[unitField] >= penny) {
                                applyAdjustment(bigItem, -penny);
                                applyAdjustment(smallItem, penny);
                                difference = 0; // Fixed difference exactly
                                break;
                            }
                        }
                        if (difference === 0) break;
                    }
                }

                // Return final allocations with just the guid and updated unit value field
                return roundedAllocations.map(({ guid, [unitField]: value }) => ({
                    guid,
                    [unitField]: value
                }));
            };

            // For upfront (non-recurring) items: filter out zero quantity and allocate pennies without recurring multiplier
            if (unitField.toLowerCase().includes("upfront")) {
                itemUpdates = allocatePennies(value, items.filter(item => item.quantity !== 0), originalUnitField, "quantity");
            } else {
                // For recurring items: filter valid recurring items with quantity > 0 and allocate pennies with multipliers
                const filteredItems = items.filter(item => item.recurringIntervalMonths > 0 && item.recurringNumPayments > 0 && item.quantity !== 0);
                itemUpdates = allocatePennies(value, filteredItems, originalUnitField, "quantity", "recurringIntervalMonths", "recurringNumPayments");
            }
        }

        // If unitField was derived, update the original items with new allocated unit values
        if (unitField !== "") {  
            const itemsToSave = itemUpdates
                .filter(i => items.some(item => item.guid === i.guid))
                .map(update => {
                    const originalItem = items.find(i => i.guid === update.guid);
                    return {
                        ...originalItem,
                        [unitField]: update[unitField]
                    };
                });

                updateQuoteItems(itemsToSave);            
        }
    }, [updateQuoteItems]);    


    /**
     * Propagates a field change made on a group row down to all child items within that group.
     *
     * When a user edits a calculated field on a group header (e.g. margin %, discount %, price, cost),
     * this function finds all quote items belonging to that group and updates each one individually
     * so that the group-level value is achieved in aggregate.
     *
     * Supported field types:
     *   - margin (e.g. "upfrontMargin", "recurringMargin"):
     *       Recalculates each item's unit price from its unit cost using the target margin %.
     *       Price is rounded up to the nearest penny to ensure the margin is always met or exceeded.
     *
     *   - discountPercent (e.g. "upfrontPriceDiscountPercent", "recurringPriceDiscountPercent"):
     *       Recalculates each item's unit price by applying the discount % to its original price.
     *       Price is rounded up to the nearest penny to ensure the discount is not exceeded.
     *
     *   - price / cost (e.g. "upfrontPrice", "recurringCost"):
     *       Spreads the total value proportionally across all items in the group
     *       via the `spreadValueToItems` helper.
     *
     * @param {object} group  - The group row object. Must have a `guid` property that matches either
     *                          `quoteModuleGuid` (module groups) or `quoteGroupDescription` (manual groups).
     * @param {string} field  - The column key being edited (e.g. "upfrontMargin", "recurringPriceDiscountPercent").
     * @param {number} value  - The new value entered by the user (margin %, discount %, or absolute price/cost).
     */
    const updateChildItems = useCallback((group, field, value) => {
        let groupItems = []

        if (quoteItems.find(x => x.quoteModuleGuid === group.guid)) {
            //This is a module group
            groupItems = quoteItems.filter(x => x.quoteModuleGuid === group.guid)
        } else if (quoteItems.find(x => x.quoteGroupDescription === group.guid)) {
            //This is a manually added group
            groupItems = quoteItems.filter(x => x.quoteGroupDescription === group.guid)
        }

        if (groupItems.length === 0) {
            toast.error("Item " + group.guid + " not found")
            return;
        }

        // Only search for the child items with in current group that match any filtering applied, 
        // to avoid updating items that are not currently visible in the table
        const filteredChildIds = group.items.map(i => i.guid);
        let items = groupItems.filter(i => filteredChildIds.includes(i.guid)) 

        if (field === "disabled") {
            // Basket processing items are system generated and must always remain included.
            items = items.filter(item => !isBasketProcessingItem(item));
        } else if (field === "isCreditItem") {
            // Basket processing items should not have credit item property modified by group-level changes.
            items = items.filter(item => !isBasketProcessingItem(item));
        }

        if (items.length === 0) {
            return;
        } else if (field.toLowerCase().includes("margin")) {
            // Changing margin on a group of items

            const unitCostField = field.toLowerCase().includes("upfront") ? "upfrontCost" : "recurringCost";
            const unitPriceField = field.toLowerCase().includes("upfront") ? "upfrontPrice" : "recurringPrice";

            const itemsUpdatedWithNewMargin = items.map(item => {
                let newPrice = item[unitCostField] === 0
                    ? 0
                    : item[unitCostField] / (1 - value / 100)
                // Round up to nearest penny, to ensure that we always meet or exceed the specified margin
                newPrice = (Math.ceil(newPrice * 100) / 100)
                return {
                    ...item,
                    [unitPriceField]: newPrice
                }
            })

            updateQuoteItems(itemsUpdatedWithNewMargin)

        } else if (field.toLowerCase().includes("discountpercent")) {
            // Discounting a group of items

            const unitCostField = field.toLowerCase().includes("upfront") ? "upfrontCost" : "recurringCost";
            const unitPriceField = field.toLowerCase().includes("upfront") ? "upfrontPrice" : "recurringPrice";
            const originalUnitPriceField = "original" + unitPriceField.charAt(0).toUpperCase() + unitPriceField.slice(1);

            const itemsUpdatedWithNewDiscount = items.map(item => {
                let newPrice = item[originalUnitPriceField] * (1 - value / 100)

                // Round up to nearest penny, to ensure that we don't exceed the specified discount
                newPrice = (Math.ceil(newPrice * 100) / 100)

                return({
                    ...item,
                    [unitPriceField]: newPrice
                })
            })

            updateQuoteItems(itemsUpdatedWithNewDiscount)
        } else if (field.toLowerCase().includes("cost") || field.toLowerCase().includes("price")) {
            spreadValueToItems(field, value, items)
        } else {
            updateQuoteItems(items.map(item => ({ ...item, [field]: value })))
        }
    }, [quoteItems, updateQuoteItems, spreadValueToItems, isBasketProcessingItem])

    /**
     * Translates a field change on a single item row into the correct underlying field update,
     * handling the conversion from display-level fields (totals, margins, discounts, annual values)
     * down to the unit-level fields stored on the item.
     *
     * This is the counterpart to `updateDependentItems` — where that function fans out a group-level
     * change to all child items, this function handles a change on a single leaf item.
     *
     * Supported field types and their behaviour:
     *   - total price / cost (e.g. "totalUpfrontPrice", "totalRecurringCost"):
     *       Divides the total by quantity (and num payments × interval months for recurring)
     *       to derive the new unit value, then saves it to the corresponding unit field.
     *
     *   - annual price / cost (e.g. "annualCost", "annualPrice"):
     *       Converts from annual to per-payment recurring value using quantity and interval months,
     *       then saves to the corresponding "recurring" unit field.
     *
     *   - margin (e.g. "upfrontMargin", "recurringMargin"):
     *       Back-calculates the unit price from the unit cost using the target margin %.
     *       Price is rounded up to the nearest penny to ensure the margin is always met or exceeded.
     *
     *   - discountPercent (e.g. "upfrontPriceDiscountPercent"):
     *       Applies the discount % to the original unit price to derive the new price.
     *
     *   - discountValue (e.g. "totalUpfrontPriceDiscountValue"):
     *       Subtracts the discount value from the original price, spreading across quantity / payments
     *       if the field is a total variant.
     *
     *   - all other fields:
     *       Written directly to the item as-is (e.g. "quantity", "internalName", "salesforceCode").
     *
     * If `value` is not a finite number, shows an error toast and does not perform any update.
     * If the item cannot be found in `quoteItems`, shows an error toast and does not perform any update.
     *
     * @param {object} item   - The row object for the item being edited. Must have `guid` and `quoteModuleGuid`.
     * @param {string} field  - The column key being edited (e.g. "totalRecurringPrice", "upfrontMargin").
     * @param {number} value  - The new value entered by the user.
     */
    const updateField = useCallback((item, field, value) => {
        var itemToEdit = quoteItems.find(x => x.guid === item.guid && x.quoteModuleGuid === item.quoteModuleGuid)

        if (!itemToEdit) {
            toast.error("Item not found: " + item.guid);
            return;
        };

        if (itemToEdit) {
            if (field === "disabled" && isBasketProcessingItem(itemToEdit)) {
                return;
            }

            if (field.startsWith("total") && itemToEdit.quantity !== 0 && !field.toLowerCase().includes("discount") &&
                (field.toLowerCase().includes("price") || field.toLowerCase().includes("cost"))) {

                if (!isFinite(value)) {
                    toast.error("Invalid value: " + value);
                    return;
                }

                const unitField = field.replace("total", "").charAt(0).toLowerCase() + field.replace("total", "").slice(1);

                let newUnitValue = value

                if (unitField.toLowerCase().includes("upfront")) {
                    newUnitValue /= itemToEdit.quantity
                } else if (unitField.toLowerCase().includes("recurring") && itemToEdit.recurringIntervalMonths > 0 && itemToEdit.recurringNumPayments > 0) {
                    newUnitValue /= itemToEdit.quantity * itemToEdit.recurringNumPayments
                }

                if (unitField.toLowerCase().includes("price")) {
                    newUnitValue = Number(newUnitValue.toFixed(2))
                }

                updateQuoteItem({
                    ...itemToEdit,
                    [unitField]: newUnitValue
                })
            } else if (field.startsWith("annual") && itemToEdit.quantity !== 0) {
                if (!isFinite(value)) {
                    toast.error("Invalid value: " + value);
                    return;
                }

                const unitField = field.replace("annual", "recurring");

                let newUnitValue = value
                newUnitValue /= itemToEdit.quantity * (12 / itemToEdit.recurringIntervalMonths)

                if (unitField.toLowerCase().includes("price")) {
                    newUnitValue = Number(newUnitValue.toFixed(2))
                }

                updateQuoteItem({
                    ...itemToEdit,
                    [unitField]: newUnitValue
                })
            } else if (field.toLowerCase().includes("margin")) {
                if (!isFinite(value)) {
                    toast.error("Invalid value: " + value);
                    return;
                }

                const unitCostField = field.toLowerCase().includes("upfront") ? "upfrontCost" : "recurringCost";
                const unitPriceField = field.toLowerCase().includes("upfront") ? "upfrontPrice" : "recurringPrice";
                let newPrice = itemToEdit[unitCostField] === 0
                    ? 0
                    : itemToEdit[unitCostField] / (1 - value / 100)

                // Round up to nearest penny, to ensure that we always meet or exceed the specified margin
                newPrice = (Math.ceil(newPrice * 100) / 100)

                updateQuoteItem({
                    ...itemToEdit,
                    [unitPriceField]: newPrice
                })
            } else if (field.toLowerCase().includes("discount")) {
                if (!isFinite(value)) {
                    toast.error("Invalid value: " + value);
                    return;
                }

                const unitCostField = field.toLowerCase().includes("upfront") ? "upfrontCost" : "recurringCost";
                const unitPriceField = field.toLowerCase().includes("upfront") ? "upfrontPrice" : "recurringPrice";
                const originalUnitPriceField = "original" + unitPriceField.charAt(0).toUpperCase() + unitPriceField.slice(1);

                let newPrice = itemToEdit[originalUnitPriceField]

                if (field.toLowerCase().includes("discountpercent")) {
                    newPrice *= (1 - value / 100)
                } else if (field.toLowerCase().includes("discountvalue")) {
                    if (field.toLowerCase().includes("recurring")) {
                        newPrice -= value / (field.includes("total") ? itemToEdit.quantity * itemToEdit.recurringNumPayments : 1)
                    } else {
                        newPrice -= value / (field.includes("total") ? itemToEdit.quantity : 1)
                    }
                }

                // Round up to nearest penny, to ensure that we don't exceed the specified discount
                newPrice = (Math.ceil(newPrice * 100) / 100)

                updateQuoteItem({
                    ...itemToEdit,
                    [unitPriceField]: newPrice
                })
            } else {
                // When changing upfront/recurring SF code, also derive the blended salesforceCode atomically
                const sfCodeUpdate = {};
                if (field === "upfrontSalesforceCode") {
                    const recurringCode = itemToEdit.isRecurring ? (itemToEdit.recurringSalesforceCode ?? "") : "";
                    if (value && recurringCode && value !== recurringCode) {
                        sfCodeUpdate.salesforceCode = `${value} / ${recurringCode}`;
                    } else {
                        sfCodeUpdate.salesforceCode = value || recurringCode || null;
                    }
                } else if (field === "recurringSalesforceCode") {
                    const upfrontCode = itemToEdit.isUpfront ? (itemToEdit.upfrontSalesforceCode ?? "") : "";
                    if (upfrontCode && value && upfrontCode !== value) {
                        sfCodeUpdate.salesforceCode = `${upfrontCode} / ${value}`;
                    } else {
                        sfCodeUpdate.salesforceCode = upfrontCode || value || null;
                    }
                }

                updateQuoteItem({
                    ...itemToEdit,
                    [field]: value,
                    ...sfCodeUpdate
                })
            }
        } 
    }, [quoteItems, updateQuoteItem, isBasketProcessingItem])     

    const editRow = useCallback((row, field, value) => {
        if (row.items) {
            // This is a group row - update all child items
            updateChildItems(row, field, value);
        } else {
            // This is a leaf item row, need to update it directly            
            updateField(row, field, value);
        }
    }, [updateChildItems, updateField, updateQuoteItem]) 

    return [editRow];
}